import JSZip from 'jszip';
import Papa from 'papaparse';
import type {
  ExamType,
  ValidateExamImportRow,
} from '../domain/entities/content.entity';
import {
  MAX_IMAGE_BYTES,
  MAX_IMAGE_LABEL,
  formatMegabytes,
} from './image-preflight';

/**
 * Leitura e validação (passada 1) do arquivo de importação de prova — o lado do
 * fluxo que **só o front consegue fazer**: o backend nunca vê o zip.
 *
 * Formato do arquivo em
 * `primum-core-root/docs/plans/[GEOVANNI] exam-csv-import-plan/csv-format.md`.
 * O que **não** está aqui é deliberado: regra de domínio de questão (mínimo de
 * alternativas, `correct_alternative` que não casa com nenhuma alternativa,
 * corpo vazio) é validada pelo `QuestionHandler` via
 * `POST /admin/content/exams/validate-import` — duplicar aqui criaria duas
 * fontes de verdade. Esta passada cobre estrutura do arquivo e o que precisa
 * estar certo para o payload sequer ser aceito pelo protocolo.
 */

/** Pasta (dentro do zip) onde as imagens citadas pelos tokens moram. */
const IMAGES_DIR = 'imagens';

const REQUIRED_COLUMNS = [
  'position',
  'institution',
  'exam_year',
  'exam_variant',
  'exam_type',
  'question_format',
  'stem',
] as const;

/** `alt_a_text` .. `alt_e_text` — célula vazia = alternativa não existe. */
const ALTERNATIVE_COLUMNS = [
  'alt_a_text',
  'alt_b_text',
  'alt_c_text',
  'alt_d_text',
  'alt_e_text',
] as const;

const KNOWN_COLUMNS: string[] = [
  ...REQUIRED_COLUMNS,
  ...ALTERNATIVE_COLUMNS,
  'correct_alternative',
  // Opcional: questão sem classificação importa como sempre importou. Precisa
  // estar aqui para não cair em "colunas ignoradas" — uma coluna conhecida que
  // ficasse fora desta lista seria silenciosamente descartada, e a prova
  // chegaria sem assunto nenhum sem ninguém notar.
  'classification',
  // O comentário oficial do time e quem o escreveu. Duas colunas: o autor é
  // campo próprio do comentário, não um prefixo dentro do texto.
  'comment',
  'comment_author',
  // Só `ESSAY`: a resposta que o corretor espera. Parte do body da questão (não
  // um content item à parte como o comentário), então as imagens dela contam no
  // teto da questão.
  'expected_answer',
];

const QUESTION_FORMATS = ['MULTIPLE_CHOICE', 'ESSAY'] as const;

/**
 * Como a coluna `exam_type` pode vir escrita. Aceita o valor canônico **e** as
 * formas que o time de conteúdo usa de fato — a trilha chega no material de
 * origem como "Acesso Direto" ou "R1", não como `DIRECT_ACCESS`.
 *
 * Não é "duas formas de dizer a mesma coisa" no sentido que o formato do CSV
 * rejeita (lá o problema é DOIS CAMPOS que podem discordar): aqui é uma coluna
 * só, e isto é normalização de entrada — a mesma ideia do nome de assunto, que
 * casa ignorando caixa e acento.
 */
const EXAM_TYPE_SYNONYMS: Record<string, ExamType> = {
  direct_access: 'DIRECT_ACCESS',
  'acesso direto': 'DIRECT_ACCESS',
  r1: 'DIRECT_ACCESS',
  prerequisite: 'PREREQUISITE',
  'pre requisito': 'PREREQUISITE',
  prerequisito: 'PREREQUISITE',
  'r+': 'PREREQUISITE',
  revalida: 'REVALIDA',
  revalidate: 'REVALIDA',
};

/** Caixa, acento, hífen e espaço repetido não distinguem trilha nenhuma. */
function normalizeExamType(raw: string): ExamType | null {
  const key = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');
  return EXAM_TYPE_SYNONYMS[key] ?? null;
}

/**
 * `[](imagens/nome.png)`. Espelha `SOURCE_IMAGE_TOKEN` do monolith
 * (`content/domain/content-types/image-tokens.ts`) — mudar um exige mudar o
 * outro; o front só coleta os nomes, quem resolve o token é o backend.
 */
const SOURCE_IMAGE_TOKEN = /\[\]\(imagens\/([^)]+)\)/g;

/**
 * `(SIGLA-UF) NOME`. Espelha `INSTITUTION_LABEL` do monolith
 * (`content/domain/institutions/institution-label.ts`). A sigla é gulosa por
 * isso casar até o ÚLTIMO hífen: sigla pode conter hífen (`USP-SP`), e partir
 * no primeiro daria uma sigla que não existe no cadastro.
 *
 * Aqui só a FORMA é checada — quem resolve a instituição de verdade (existe?
 * a UF bate?) é o backend, que é quem tem o cadastro.
 */
const INSTITUTION_LABEL = /^\(([^)]+)-([A-Za-z]{2})\)\s*(.+)$/;

/**
 * Espelha `MAX_IMAGES_PER_QUESTION` de `question-image-checks.ts` no monolith. O
 * teto por imagem vive em `image-preflight.ts`, junto de quem lê os bytes.
 */
const MAX_IMAGES_PER_QUESTION = 10;

/** `line` = linha do arquivo (1 é o header); `null` = erro do arquivo inteiro. */
export interface ImportError {
  line: number | null;
  message: string;
}

/** Uma linha do CSV já convertida para o payload de questão. */
export interface ImportRowDraft {
  line: number;
  position: number;
  format: (typeof QUESTION_FORMATS)[number];
  /** Já em HTML (texto puro convertido), com os tokens de imagem intactos. */
  stem: string;
  alternatives: { id: string; text: string }[];
  correctAlternativeId?: string;
  /**
   * Só `ESSAY`, já em HTML como o `stem`. Ausente = célula vazia: o back só
   * grava o campo quando tem conteúdo.
   */
  expectedAnswer?: string;
  /** Nomes citados pelos tokens desta linha, sem repetição. */
  imageFilenames: string[];
  /**
   * A célula `classification` como veio: nomes de assunto separados por `|`, o
   * primeiro é a primária. Vai **inteira** para o back, que é quem tem o
   * cadastro de assuntos — mesma divisão da coluna `institution`.
   *
   * Aqui não há o que validar: a única checagem possível deste lado seria a
   * forma, e a forma é texto livre. "Esse assunto existe?", "esse nome é
   * ambíguo?" e "esse assunto está repetido na célula?" são todas do back
   * (passada 2), e duplicá-las aqui criaria uma segunda fonte de verdade sobre
   * a taxonomia — que o front não tem.
   */
  classification: string;
  /** Só para exibir na confirmação, na ordem do arquivo. */
  subjectNames: string[];
  /**
   * O comentário oficial, com os tokens de imagem **crus** — mesma gramática do
   * enunciado, então o mesmo token vale aqui.
   */
  comment: string;
  /** Quem do time escreveu. Texto livre; o back só normaliza espaço. */
  commentAuthor: string;
  /**
   * Nomes citados pelos tokens do COMENTÁRIO. Lista à parte da da questão
   * porque o dono do arquivo no registro é outro (o comentário) e o teto de 10
   * imagens é por body.
   */
  commentImageFilenames: string[];
}

export interface ParsedExamCsv {
  /** A coluna `institution` como veio — é ela que vai inteira para o back. */
  institutionLabel: string;
  /** Só para exibir antes do back responder; a resolução real é do back. */
  institutionInitials: string;
  institutionUf: string;
  /** O nome como veio na planilha — o deep link de cadastro pré-preenche com ele. */
  institutionName: string;
  examYear: number;
  examVariant: string;
  /** Trilha da residência, já normalizada para o valor que o back aceita. */
  examType: ExamType;
  rows: ImportRowDraft[];
  /** Colunas desconhecidas: ignoradas, não bloqueiam — só informam. */
  ignoredColumns: string[];
}

export interface ExamZipContents {
  csvFilename: string;
  csvText: string;
  /**
   * Nome do arquivo (como citado no token) → entrada do zip. Os bytes só são
   * descomprimidos pelo pré-flight e, de novo, quando a fila chega naquela
   * questão (`preparedToBase64` em `image-preflight.ts`).
   */
  images: Map<string, JSZip.JSZipObject>;
}

// ---------------------------------------------------------------------------
// Zip
// ---------------------------------------------------------------------------

/** Lixo que o Finder do macOS enfia em todo zip. */
function isJunkEntry(path: string): boolean {
  const name = path.split('/').pop() ?? '';
  return path.startsWith('__MACOSX') || name.startsWith('._');
}

/**
 * Abre o zip e separa o CSV das imagens. Aceita `Blob` (e não só `File`) porque
 * o reenvio de um lote interrompido reabre o arquivo guardado no IndexedDB, que
 * não tem nome nem data. Tolera o material vindo dentro de uma
 * pasta (`prova/prova.csv` + `prova/imagens/…`) — é o que acontece quando se
 * compacta a pasta em vez do conteúdo dela, e recusar isso só geraria suporte.
 */
export async function readExamZip(file: Blob): Promise<ExamZipContents> {
  const zip = await JSZip.loadAsync(file);

  const entries = Object.values(zip.files).filter(
    (entry) => !entry.dir && !isJunkEntry(entry.name),
  );

  const csvEntries = entries
    .filter((entry) => entry.name.toLowerCase().endsWith('.csv'))
    // O mais raso primeiro: se houver mais de um, o da raiz é o da prova.
    .sort((a, b) => a.name.split('/').length - b.name.split('/').length);

  if (csvEntries.length === 0) {
    throw new Error('O zip não contém nenhum arquivo .csv.');
  }

  const images = new Map<string, JSZip.JSZipObject>();
  for (const entry of entries) {
    const segments = entry.name.split('/');
    const filename = segments.pop() ?? '';
    if (segments.pop()?.toLowerCase() !== IMAGES_DIR) continue;
    images.set(filename, entry);
  }

  const csvEntry = csvEntries[0]!;
  return {
    csvFilename: csvEntry.name,
    csvText: await csvEntry.async('string'),
    images,
  };
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Célula do CSV → texto rico. A planilha traz **texto puro**: quem preenche não
 * escreve HTML, então tudo é escapado antes; linha em branco vira parágrafo
 * novo e quebra simples vira `<br>` (o allowlist do `sanitizeRichText` do back
 * aceita `p` e `br`). O token de imagem não tem caractere escapável, então
 * atravessa intacto.
 */
function plainTextToRichHtml(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\r?\n[ \t]*\r?\n/)
    .map(
      (paragraph) =>
        `<p>${escapeHtml(paragraph.trim()).replace(/\r?\n/g, '<br>')}</p>`,
    )
    .join('');
}

/** Nomes de arquivo citados por tokens de imagem, na ordem, sem repetição. */
function collectImageFilenames(text: string): string[] {
  const found = new Set<string>();
  for (const match of text.matchAll(SOURCE_IMAGE_TOKEN)) {
    found.add(match[1]!);
  }
  return [...found];
}

function cell(row: Record<string, string>, column: string): string {
  return (row[column] ?? '').trim();
}

/**
 * Espelha `parseClassificationLabel` do monolith
 * (`content/domain/subjects/subject-classification-label.ts`) — mudar um exige
 * mudar o outro.
 *
 * A cópia é **só para exibir** (contar quantas questões vêm classificadas, e
 * listar os assuntos citados na tela de confirmação). A célula que vai para o
 * back é a string crua: quem resolve nome → assunto é o back, e o front não
 * decide nada a partir desta lista.
 */
function splitClassification(cellValue: string): string[] {
  return cellValue
    .split('|')
    .map((name) => name.trim().replace(/\s+/g, ' '))
    .filter((name) => name.length > 0);
}

/**
 * Passada 1: estrutura do arquivo. Devolve `parsed: null` sempre que houver
 * erro — nada é enviado ao backend com o arquivo em estado duvidoso.
 */
export function parseExamCsv(csvText: string): {
  parsed: ParsedExamCsv | null;
  errors: ImportError[];
} {
  const errors: ImportError[] = [];
  // BOM do Excel entraria no nome da primeira coluna.
  const text = csvText.replace(/^﻿/, '');

  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) => header.trim().toLowerCase(),
  });

  for (const error of result.errors) {
    errors.push({
      line: typeof error.row === 'number' ? error.row + 2 : null,
      message: error.message,
    });
  }

  const headers = result.meta.fields ?? [];
  const missing = REQUIRED_COLUMNS.filter(
    (column) => !headers.includes(column),
  );
  if (missing.length > 0) {
    errors.push({
      line: 1,
      message: `Colunas obrigatórias ausentes: ${missing.join(', ')}.`,
    });
  }
  const ignoredColumns = headers.filter(
    (header) => header !== '' && !KNOWN_COLUMNS.includes(header),
  );

  if (result.data.length === 0) {
    errors.push({
      line: null,
      message: 'O CSV não tem nenhuma linha de dados.',
    });
  }
  if (errors.length > 0) return { parsed: null, errors };

  const rows: ImportRowDraft[] = [];
  const positions = new Map<number, number>();

  result.data.forEach((raw, index) => {
    const line = index + 2;

    const rawPosition = cell(raw, 'position');
    const position = Number(rawPosition);
    if (!Number.isInteger(position) || position < 1) {
      errors.push({
        line,
        message: `position inválida ("${rawPosition}"): use um inteiro ≥ 1.`,
      });
      return;
    }
    const duplicateOf = positions.get(position);
    if (duplicateOf !== undefined) {
      errors.push({
        line,
        message: `position ${position} repetida (já usada na linha ${duplicateOf}).`,
      });
      return;
    }
    positions.set(position, line);

    const rawFormat = cell(raw, 'question_format').toUpperCase();
    if (!QUESTION_FORMATS.includes(rawFormat as ImportRowDraft['format'])) {
      errors.push({
        line,
        message: `question_format inválido ("${cell(raw, 'question_format')}"): use ${QUESTION_FORMATS.join(' ou ')}.`,
      });
      return;
    }
    const format = rawFormat as ImportRowDraft['format'];

    const rawStem = raw.stem ?? '';
    const rawAlternatives = ALTERNATIVE_COLUMNS.map((column, letterIndex) => ({
      // Id da alternativa = a letra da coluna, minúscula (mesma forma que o
      // resto do banco já usa em `alternatives[].id`).
      id: String.fromCharCode(97 + letterIndex),
      raw: raw[column] ?? '',
    })).filter((alternative) => alternative.raw.trim() !== '');

    // Recusada, não descartada: a célula preenchida numa múltipla escolha é
    // quase sempre a linha com o formato errado, e descartar em silêncio subiria
    // a dissertativa como objetiva sem ninguém notar.
    const rawExpectedAnswer = raw.expected_answer ?? '';
    if (format === 'MULTIPLE_CHOICE' && rawExpectedAnswer.trim() !== '') {
      errors.push({
        line,
        message:
          'expected_answer preenchida numa questão MULTIPLE_CHOICE — a resposta esperada só vale para ESSAY.',
      });
      return;
    }

    const imageFilenames = [
      ...new Set(
        [
          rawStem,
          ...rawAlternatives.map((a) => a.raw),
          rawExpectedAnswer,
        ].flatMap(collectImageFilenames),
      ),
    ];
    const expectedAnswer = plainTextToRichHtml(rawExpectedAnswer);

    const correct = cell(raw, 'correct_alternative').toLowerCase();
    const classification = cell(raw, 'classification');
    const comment = raw.comment ?? '';

    rows.push({
      line,
      position,
      format,
      stem: plainTextToRichHtml(rawStem),
      alternatives:
        format === 'MULTIPLE_CHOICE'
          ? rawAlternatives.map((alternative) => ({
              id: alternative.id,
              text: plainTextToRichHtml(alternative.raw),
            }))
          : [],
      correctAlternativeId: correct || undefined,
      expectedAnswer: expectedAnswer || undefined,
      imageFilenames,
      classification,
      subjectNames: splitClassification(classification),
      comment,
      commentAuthor: cell(raw, 'comment_author'),
      commentImageFilenames: collectImageFilenames(comment),
    });
  });

  // Metadados da prova vêm repetidos em toda linha: uma importação é uma prova
  // só, então divergência entre linhas é erro, não "a última ganha".
  const first = result.data[0]!;
  const institutionLabel = cell(first, 'institution');
  const examVariant = cell(first, 'exam_variant');
  const examYear = Number(cell(first, 'exam_year'));
  const rawExamType = cell(first, 'exam_type');
  const examType = normalizeExamType(rawExamType);

  const institutionParts = institutionLabel.match(INSTITUTION_LABEL);
  if (!institutionLabel) {
    errors.push({ line: 2, message: 'institution está vazia.' });
  } else if (!institutionParts) {
    errors.push({
      line: 2,
      message: `institution ("${institutionLabel}") fora do formato "(SIGLA-UF) NOME" — ex.: (UNICAMP-SP) Universidade Estadual de Campinas.`,
    });
  }
  if (!examVariant) {
    errors.push({
      line: 2,
      message: 'exam_variant está vazia (é o complemento da prova).',
    });
  }
  if (!Number.isInteger(examYear)) {
    errors.push({
      line: 2,
      message: `exam_year inválido ("${cell(first, 'exam_year')}"): use um ano inteiro.`,
    });
  }
  if (examType === null) {
    errors.push({
      line: 2,
      message: rawExamType
        ? `exam_type inválido ("${rawExamType}"): use acesso direto (R1), pré-requisito (R+) ou revalida.`
        : 'exam_type está vazia (a trilha da residência: acesso direto, pré-requisito ou revalida).',
    });
  }

  result.data.forEach((raw, index) => {
    if (index === 0) return;
    const line = index + 2;
    if (
      cell(raw, 'institution').toLowerCase() !== institutionLabel.toLowerCase()
    ) {
      errors.push({
        line,
        message: `institution ("${cell(raw, 'institution')}") difere da primeira linha ("${institutionLabel}") — uma importação é uma prova só.`,
      });
    }
    if (Number(cell(raw, 'exam_year')) !== examYear) {
      errors.push({
        line,
        message: `exam_year ("${cell(raw, 'exam_year')}") difere da primeira linha ("${examYear}").`,
      });
    }
    if (cell(raw, 'exam_variant') !== examVariant) {
      errors.push({
        line,
        message: `exam_variant ("${cell(raw, 'exam_variant')}") difere da primeira linha ("${examVariant}").`,
      });
    }
    // Compara a trilha JÁ normalizada: "R1" e "Acesso Direto" na mesma planilha
    // são a mesma prova, e acusar divergência aí seria erro inventado.
    if (normalizeExamType(cell(raw, 'exam_type')) !== examType) {
      errors.push({
        line,
        message: `exam_type ("${cell(raw, 'exam_type')}") difere da primeira linha ("${rawExamType}") — uma importação é uma prova só.`,
      });
    }
  });

  if (errors.length > 0) return { parsed: null, errors };

  return {
    parsed: {
      institutionLabel,
      institutionInitials: institutionParts![1]!.trim().toUpperCase(),
      institutionUf: institutionParts![2]!.toUpperCase(),
      institutionName: institutionParts![3]!.trim(),
      examYear,
      examVariant,
      examType: examType!,
      rows,
      ignoredColumns,
    },
    errors: [],
  };
}

/**
 * As checagens de imagem que **só o front consegue fazer**: o backend nunca
 * recebe o zip, então nada disso é verificável do outro lado — e descobrir
 * qualquer uma no meio da fila de upload deixaria a prova pela metade.
 *
 * O vínculo é conferido nos dois sentidos, como no monolith: token sem arquivo
 * salvaria a questão com o token cru visível; arquivo sem token seria um upload
 * que ninguém nunca vê.
 *
 * O que **não** está aqui: o formato real das imagens. Isso exige descomprimir
 * os bytes e mora em `image-preflight.ts` (`preflightImages`), que roda logo
 * depois desta função — separado porque é assíncrono, usa API de browser
 * (`createImageBitmap`/canvas) e produz pendência com ação de correção, não uma
 * linha de erro.
 */
export function validateImages(
  rows: ImportRowDraft[],
  images: ReadonlyMap<string, JSZip.JSZipObject>,
): ImportError[] {
  const errors: ImportError[] = [];
  const cited = new Set<string>();

  for (const row of rows) {
    // O comentário entra na MESMA varredura: um arquivo citado só pelo
    // comentário seria acusado de "está no zip e ninguém cita", e um token do
    // comentário apontando para arquivo ausente passaria batido até o meio da
    // fila de upload.
    for (const filename of [
      ...row.imageFilenames,
      ...row.commentImageFilenames,
    ]) {
      cited.add(filename);
      const entry = images.get(filename);
      if (!entry) {
        errors.push({
          line: row.line,
          message: `imagem "${filename}" citada no texto não existe em ${IMAGES_DIR}/ dentro do zip.`,
        });
        continue;
      }
      // `_data.uncompressedSize` é o tamanho real sem descomprimir o arquivo —
      // conferir aqui evita carregar 40 MB em memória só para recusar depois.
      const size = (
        entry as unknown as { _data?: { uncompressedSize?: number } }
      )._data?.uncompressedSize;
      if (typeof size === 'number' && size > MAX_IMAGE_BYTES) {
        errors.push({
          line: row.line,
          message: `imagem "${filename}" tem ${formatMegabytes(size)} — o limite é ${MAX_IMAGE_LABEL} por imagem. Reduza o arquivo e refaça o zip.`,
        });
      }
    }

    // O teto é por BODY, não pela soma: o comentário é um content item próprio,
    // com body próprio, e é assim que o back o aplica (`assertQuestionImageFilenames`
    // roda uma vez para os textos da questão e outra para o do comentário).
    if (row.imageFilenames.length > MAX_IMAGES_PER_QUESTION) {
      errors.push({
        line: row.line,
        message: `a questão cita ${row.imageFilenames.length} imagens — o limite é ${MAX_IMAGES_PER_QUESTION} por questão.`,
      });
    }
    if (row.commentImageFilenames.length > MAX_IMAGES_PER_QUESTION) {
      errors.push({
        line: row.line,
        message: `o comentário cita ${row.commentImageFilenames.length} imagens — o limite é ${MAX_IMAGES_PER_QUESTION}.`,
      });
    }
  }

  for (const filename of images.keys()) {
    if (!cited.has(filename)) {
      errors.push({
        line: null,
        message: `imagem "${filename}" está em ${IMAGES_DIR}/ mas nenhuma questão a cita — seria enviada e nunca exibida.`,
      });
    }
  }

  return errors;
}

/** Payload do dry-run (passada 2): texto puro, sem nenhum byte de imagem. */
export function toValidateImportRows(
  rows: ImportRowDraft[],
): ValidateExamImportRow[] {
  return rows.map((row) => ({
    position: row.position,
    format: row.format,
    stem: row.stem,
    alternatives:
      row.format === 'MULTIPLE_CHOICE' ? row.alternatives : undefined,
    correctAlternativeId: row.correctAlternativeId,
    expectedAnswer: row.expectedAnswer,
    imageFilenames: row.imageFilenames,
    classification: row.classification || undefined,
    comment: row.comment.trim() || undefined,
    commentAuthor: row.commentAuthor || undefined,
    commentImageFilenames:
      row.commentImageFilenames.length > 0
        ? row.commentImageFilenames
        : undefined,
  }));
}
